import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {Loader, Send} from "react-feather";
import {debounceTime, from, fromEvent} from "rxjs";
import {
    deleteConversationFile,
    getConversationDetails,
    postConversationMessage
} from "../../services/api/messaging";
import {map} from "rxjs/operators";
import {
    getConversationAvatar,
    getConversationParticipants, formatLinksInText
} from "../../services/helpers";
import {MESSAGE_RELOAD, ACTIVE_CONVERSATION_CHANGED} from "../../services/events";
import _debounce from 'lodash/debounce';
import UserAvatar from "../../components/Item/UserAvatar";
import MessageItem from "../../components/Item/MessageItem";
import LoadSpinner from "../../components/Common/LoadSpinner";
import FileUpload from "../../components/Input/FileUpload";
import Notification from "../../components/Common/Notification";
import {BROKER, VENDOR} from "../../services/constants/CompanyType";
import {MESSAGE_MAX} from "../../services/constants/MaxLenght";
import FileList from "../../components/Input/FileList";
import LinkFormatterInput from "../../components/Common/LinkFormatterInput";
import MessageActionDropdown from "../../components/Messaging/MessageActionDropdown";
import ForwardIcon from "../../images/svg/message/flip-forward.svg";
import ForwardModal from "../../components/Modal/ForwardModal";
import Tooltip from "../../components/Common/Tooltip";
import LogoAvatar from "../../components/Item/LogoAvatar";
import {moment} from "../../services/moment-extensions";
import {MESSAGE_USER_ADDED, MESSAGE_USER_FORWARDED, MESSAGE_USER_REMOVED} from "../../services/constants/MessageStatus";

const ConversationMessaging = memo(function ConversationMessaging({
                                                                      authData,
                                                                      activeConversation,
                                                                      onHandleMessageSent,
                                                                      onHandleConversationArchived,
                                                                      onHandleConversationSyncUsers
                                                                  }) {
    let messageDateDivider = false;
    const [forwardModal, setForwardModal] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationType, setNotificationType] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loadedPage, setLoadedPage] = useState(0);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [fileUploadKey, setFileUploadKey] = useState(Date.now());
    const [openParticipantsModal, setOpenParticipantsModal] = useState(false);

    useEffect(() => {
        setCurrentPage(1);
        setLoadedPage(0);
        setConversationMessages([]);
    }, [activeConversation.id]);

    const messageRef = useRef(null);
    const [messageContent, setMessageContent] = useState('');
    const conversationMessagesRef = useRef(null);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [conversationParticipants, setConversationParticipants] = useState([]);
    const [conversationMetaData, setConversationMetaData] = useState([]);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const [files, setFiles] = useState([]);

    const handleScroll = _debounce(() => {
        const {scrollTop} = conversationMessagesRef.current;
        if (scrollTop <= 300) {
            if (currentPage < conversationMetaData.pagination?.total_pages) {
                if (!loadingMoreMessages) {
                    setLoadingMoreMessages(true);
                    setCurrentPage((prevPage) => prevPage + 1);
                }
            }
        }
    }, 100);

    const fetchConversationMessages = useCallback(() => {
        return from(getConversationDetails(activeConversation.id, currentPage))
            .pipe(
                map(response => response)
            )
            .subscribe(
                result => {

                    if (!result) {
                        setConversationMessages([]);
                        setCurrentPage(0)
                        setLoadingMoreMessages(false);
                        return;
                    }

                    let metaData = result.messages.meta;
                    let pagination = metaData.pagination;
                    if (currentPage > 1) {
                        setConversationMessages((prevMessages) => {
                            return checkMessagesForForward([...prevMessages, ...result.messages.data]);
                        });
                    } else {
                        setConversationMessages(checkMessagesForForward(result.messages.data));
                    }
                    setConversationMetaData(metaData);
                    setCurrentPage(pagination.current_page);
                    setLoadedPage(currentPage);
                    setConversationParticipants(result.participants);
                    setLoadingMoreMessages(false);
                },
                error => {
                    console.error('Error fetching messages:', error);
                }
            );
    }, [activeConversation.id, currentPage]);

    const checkMessagesForForward = (messages) => {
        const tempMessages = [];

        messages.map((message) => {

            if (tempMessages.length === 0) {
                tempMessages.push(message);
            } else {
                const lastMessage = tempMessages[tempMessages.length - 1];
                const currentMessageDate = moment(message.created_at * 1000);
                const lastMessageDate = moment(lastMessage.created_at * 1000);
                const diff = lastMessageDate.diff(currentMessageDate, 'seconds');

                if (message.type === MESSAGE_USER_ADDED && lastMessage.type === MESSAGE_USER_REMOVED && Math.abs(diff) < 20 &&
                    lastMessage.content.includes(`${lastMessage.account.first_name} ${lastMessage.account.last_name}`)) {
                    message.type = MESSAGE_USER_FORWARDED;
                    tempMessages[tempMessages.length - 1] = message;
                } else {
                    tempMessages.push(message);
                }
            }

            return true;

        });


        return tempMessages;


    }

    useEffect(() => {
        fromEvent(document, ACTIVE_CONVERSATION_CHANGED).subscribe(() => {
            setMessageContent("");
            setFiles([]);
        });

        fromEvent(document, MESSAGE_RELOAD).subscribe(() => {
            setMessageContent("");
            setFiles([]);
            setFileUploadKey(Date.now());
            setLoadedPage(0);
            setCurrentPage(1);
        });

        if (activeConversation) {
            if (currentPage > loadedPage) {
                setLoadedPage(currentPage);
                fetchConversationMessages();
            }

            const messageSentEventObserver = fromEvent(document, 'scroll_message');
            const debouncedConversationChangeObservable = messageSentEventObserver.pipe(debounceTime(100));

            const messageSentEventSubscription = debouncedConversationChangeObservable.subscribe(() => {
                setLoadedPage(0);
                setCurrentPage(1);
                fetchConversationMessages();
            });

            return () => {
                messageSentEventSubscription.unsubscribe();
            };
        }
    }, [activeConversation, currentPage, loadedPage, fetchConversationMessages]);
    const scrollToBottom = () => {
        const container = conversationMessagesRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    };

    // Scroll to the bottom whenever 'content' changes
    useEffect(() => {
        scrollToBottom();
    }, [conversationMessages]);

    useEffect(() => {
        const container = conversationMessagesRef.current;
        if (container.scrollTop === 0) {
            scrollToBottom();
        }
        conversationMessagesRef.current.addEventListener('scroll', handleScroll);
        return () => {
            if (conversationMessagesRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                conversationMessagesRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [currentPage, handleScroll, loadedPage]);

    const handleSendMessage = async (event) => {
        event.preventDefault();
        if (sendingMessage) {
            return false;
        }
        setSendingMessage(true);

        const payload = {
            'content': formatLinksInText(messageContent, true),
        };
        try {
            const currentTimestamp = Math.floor(Date.now() / 1000);
            let newMessage = {
                account: {
                    id: authData.id,
                    first_name: authData.first_name,
                    last_name: authData.last_name,
                    avatar: authData.avatar,
                    type: authData.type
                },
                content: formatLinksInText(messageContent, true),
                created_at: currentTimestamp,
                id: 0
            };
            const validFiles = files.filter((f) => !f.error);
            if (validFiles.length) {
                payload['uploads'] = validFiles;
                newMessage.files = validFiles;
            }
            setConversationMessages((prevMessages) => {
                const updatedMessages = [...prevMessages];
                updatedMessages.unshift(newMessage);
                return updatedMessages;
            });

            if ((messageContent.trim()).length > MESSAGE_MAX) {
                messageRef.current.classList.add('sl-input-destructive');
                setSendingMessage(false);
                return;
            }
            await postConversationMessage(activeConversation.id, payload).then(() => {
                setFiles([]);
                setFileUploadKey(Date.now());
                setLoadedPage(0);
                setCurrentPage(1);
                activeConversation.latest_message.console = messageContent;
                activeConversation.latest_message.files = [''];
                onHandleMessageSent(activeConversation);

            });
            setSendingMessage(false);
            setMessageContent('');


        } catch (error) {
            setSendingMessage(false);
            console.error('Error fetching messages:', error);
        }
    }
    const handleMessageContentChange = (value) => {
        setMessageContent(value);

        if (value.length > MESSAGE_MAX) {
            messageRef.current.classList.add('sl-input-destructive');
        } else {
            messageRef.current.classList.remove('sl-input-destructive');
        }
    };

    const clearNotification = () => {
        setNotificationMessage('');
        setNotificationType('');
    };


    const handleFileUploading = (file) => {
        setIsUploading(true);
        setFiles((prevFiles) => {
            const existingFile = prevFiles.find((prevFile) => prevFile.name === file.name);

            if (existingFile) {
                return prevFiles.map((prevFile) =>
                    prevFile.name === file.name ? {...prevFile, ...file} : prevFile
                );
            } else {
                return [...prevFiles, file];
            }
        });
    };

    const handleFileUploaded = () => {
        setIsUploading(false);
    };

    const handleMessageRemoveFile = (fileToRemove) => {
        if (fileToRemove.error) {
            setFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile.name !== fileToRemove.name || !prevFile.error));
        } else {
            setFiles((prevFiles) => prevFiles.filter((prevFile) => prevFile.location !== fileToRemove.location));
            deleteConversationFile(fileToRemove.location).then(r => r);
        }
        setFileUploadKey(Date.now());
    };

    const headerLogo = activeConversation[authData.type === VENDOR ? BROKER : VENDOR]?.logo;


    return (<div className="sl-flex sl-flex-col sl-m-0 sl-grow-1 sl-w-full sl-relative">
        {activeConversation.length !== 0 &&
            <div className="sl-text-gray-900 sl-flex sl-gap-3 sl-grow-1 sl-px-6 sl-py-3 sl-border-bottom">
                <div className="sl-flex sl-flex-col">
                    <UserAvatar userData={getConversationAvatar(activeConversation, authData)}
                                avatarClass="sl-flex sl-h-12 sl-w-12"/>
                </div>
                <div className="sl-grow-1 sl-flex sl-flex-col sl-justify-flex-start">
                    <div className="sl-flex sl-align-center sl-justify-flex-start sl-gap-2 sl-ellipsis">
                        <div
                            className="sl-font sl-font-semibold sl-text-lg sl-text-gray-900 sl-ellipsis-one-line sl-text-left">
                            {getConversationParticipants(activeConversation, authData)}
                        </div>
                        <div className={"sl-flex sl-flex-row sl-gap-1"}>
                            {headerLogo &&
                                <div
                                    className={"sl-avatar sl-border-1 sl-border-solid sl-border-gray-200 sl-h-6 sl-w-6 "}>
                                    <LogoAvatar
                                        avatarClass="sl-h-full sl-h-6 sl-w-6 sl-text-xs sl-line-h-3 "
                                        logo={activeConversation[authData.type === VENDOR ? BROKER : VENDOR][authData.type === BROKER ? 'favico' : 'logo']}
                                        slug={activeConversation[authData.type === VENDOR ? BROKER : VENDOR]?.slug}
                                    /></div>
                            }
                            {!headerLogo && <span
                                className='sl-text-gray-700 sl-text-sm sl-font-normal sl-font sl-align-self-center'>
                                {activeConversation[authData.type === VENDOR ? BROKER : VENDOR]?.name}
                            </span>}
                        </div>
                        {activeConversation.archived &&
                            <div
                                className="sl-badge sl-badge-sm sl-text-xs sl-badge-gray sl-mr-auto ">Archived</div>}
                    </div>


                    <div
                        className="sl-flex sl-align-center sl-gap-1 sl-justify-flex-start sl-ellipsis">
                        {activeConversation.participants.length > 2 &&
                            <span onClick={() => setOpenParticipantsModal(true)}
                                  className={"sl-text-primary-700 cursor-pointer sl-font sl-text-sm sl-font-medium sl-ellipsis-one-line"}>
                                {activeConversation.participants.length} participants
                            </span>}

                        {activeConversation.participants.length <= 2 && <div
                            className="sl-font sl-text-sm sl-font-medium sl-text-gray-900 sl-ellipsis-one-line">
                            {activeConversation.topic}
                        </div>}
                    </div>
                </div>
                <div className="sl-mr-0 sl-ml-auto sl-shrink-0 sl-flex sl-align-center sl-gap-3">


                    {authData.type === VENDOR &&
                        <Tooltip content="Forward conversation">
                            <button className={"sl-cursor-pointer sl-btn-clear sl-rounded-lg sl-p-2"}
                                    onClick={() => setForwardModal(!forwardModal)}>
                                <img alt={"Forward"} src={ForwardIcon}/>
                            </button>
                        </Tooltip>}

                    {forwardModal && conversationParticipants.length ? <ForwardModal
                        participants={conversationParticipants}
                        authData={authData}
                        conversation={activeConversation}
                        onConversationSyncUsers={onHandleConversationSyncUsers}
                        handleClose={() => setForwardModal(false)}/> : <div className={"sl-hide"}/>}

                    <MessageActionDropdown
                        setOpenParticipantsModal={setOpenParticipantsModal}
                        openParticipantsModal={openParticipantsModal}
                        position={'top'}
                        conversationParticipants={conversationParticipants}
                        activeConversation={activeConversation}
                        authData={authData}
                        onHandleConversationArchived={onHandleConversationArchived}
                        setSendingMessage={setSendingMessage}
                        setMessageContent={setMessageContent}
                    />

                </div>
            </div>}

        <div className="sl-pr-1">
            <div className="sl-pr-1 sl-overflow-y-scroll sl-max-h-screen-custom sl-customized-scrollbar sl-pb-54"
                 ref={conversationMessagesRef} onScroll={handleScroll}>
                <div
                    className="sl-pb-2 sl-border-right sl-px-8 sl-py-6 sl-flex sl-flex-col sl-gap-6 sl-h-max">
                    {(conversationMessages.length !== 0) ? (
                        conversationMessages
                            .slice() // Create a copy of the array to avoid modifying the original
                            .reverse() // Reverse the order of the array
                            .map((item, index) => {
                                const showDate = (messageDateDivider !== moment(item.created_at * 1000).toDateLongWithToday());
                                messageDateDivider = moment(item.created_at * 1000).toDateLongWithToday();
                                return (
                                    <MessageItem
                                        key={index}
                                        item={item}
                                        activeConversation={activeConversation}
                                        authData={authData}
                                        showDate={showDate}
                                    />
                                );
                            })
                    ) : (
                        <div className="sl-self-center sl-mt-3 sl-mb-21"><LoadSpinner/></div>
                    )}
                </div>
            </div>
        </div>

        <div
            className="sl-absolute sl-bottom-0 sl-flex-wrap sl-bg-white sl-flex sl-gap-4 sl-grow-1 sl-px-32px sl-py-20 sl-border-top sl-grow-1 sl-w-full">
            <FileList fileList={files} onRemoveFile={handleMessageRemoveFile}/>
            <LinkFormatterInput
                placeholder="Send a Message"
                inputRef={messageRef}
                value={messageContent}
                className="sl-customized-scrollbar sl-customized-scrollbar-no-track sl-h-25 sl-grow-1 sl-w-full sl-input sl-modal-textarea sl-border-1 sl-shadow-xs sl-rounded-lg sl-font sl-font-normal sl-text-md sl-text-gray-base"
                disabled={activeConversation.archived}
                onChange={handleMessageContentChange}/>

            <div className="sl-flex sl-justify-space-between sl-align-center sl-grow-1 sl-w-full">
                <div>
                    {!activeConversation.archived &&
                        <FileUpload key={fileUploadKey} onUploading={handleFileUploading}
                                    onUploaded={handleFileUploaded}/>
                    }
                </div>

                <button onClick={handleSendMessage}
                        disabled={activeConversation.archived || isUploading || (messageContent.trim()).length > MESSAGE_MAX || (messageContent.trim() === '' && files.filter((f) => !f.error).length === 0)}
                        className="sl-btn sl-btn-primary sl-btn-md sl-text-sm sl-font-medium">
                    {sendingMessage ?
                        <div className="sl-flex sl-align-center sl-gap-2"><Loader size={20}/> Sending</div> :
                        <div className="sl-flex sl-align-center sl-gap-2"><Send size={20}/> Send</div>}
                </button>
            </div>
        </div>

        {notificationMessage.length > 0 &&
            <Notification message={notificationMessage} type={notificationType} onClose={clearNotification}/>}
    </div>);
});

export default ConversationMessaging;
